import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  const closeAll = () => {
    setMobileOpen(false);
    setCatOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-600 bg-primary-50"
        : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" onClick={closeAll} className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-vidicon-2-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
                吃瓜视频
              </span>
              <span className="hidden text-[10px] tracking-[0.2em] text-foreground-500 sm:block">
                CHIGUA TV
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <NavLink to="/" end className={navLinkClass}>
              首页
            </NavLink>
            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  catOpen
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-64 rounded-lg border border-background-200 bg-background-50 p-2 animate-fade-in">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        onClick={closeAll}
                        className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        <span className="flex h-5 w-5 items-center justify-center text-base text-primary-500">
                          <i className={cat.icon} />
                        </span>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/about" className={navLinkClass}>
              关于我们
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  name="q"
                  placeholder="搜索视频、明星、热点"
                  className="w-56 rounded-full border border-background-200 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 xl:w-64"
                />
              </div>
              <button
                type="submit"
                className="ml-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <Link
              to="/search"
              onClick={closeAll}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600 lg:hidden"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl" />
            </Link>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
              aria-label="菜单"
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 md:hidden animate-fade-in">
          <div className="px-4 py-4">
            <form onSubmit={submitSearch} className="mb-4">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  name="q"
                  placeholder="搜索视频、明星、热点"
                  className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
                />
              </div>
            </form>
            <NavLink to="/" end onClick={closeAll} className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              首页
            </NavLink>
            <NavLink to="/about" onClick={closeAll} className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100">
              关于我们
            </NavLink>
            <div className="mt-3 border-t border-background-200 pt-3">
              <p className="mb-2 px-3 text-xs font-semibold tracking-wide text-foreground-500">视频分类</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    onClick={closeAll}
                    className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                  >
                    <span className="flex h-5 w-5 items-center justify-center text-base text-primary-500">
                      <i className={cat.icon} />
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}